import { Stack, ButtonGroup, useToast } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { API_ROUTES } from "definitions/constants/routeConstants";
import apiClient from "services/ApiClient";
import { isNetworkingError } from "services/ApiClient/networkingError";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { object as yupObject, string as yupString, array as yupArray } from "yup";
import { Company } from "models/company";
import GenericAsyncSelectField from "components/FormElements/Fields/SelectFields/GenericAsyncSelectField";
import { FloatingField } from "design-system";
import ProceedCancelButtons from "../Button/ProceedCancelButtons";

const simpleEmployeeForm = yupObject({
  firstName: yupString().label("First Name").required(),
  lastName: yupString().label("Last Name").required(),
  workEmail: yupString().label("Work Email").email().required(),
  roles: yupArray().label("Roles").required(),
  companyId: yupString().label("Company").required(),
});

const FIRST_NAME_FIELD = {
  type: "text",
  label: "First Name",
  name: "firstName",
};

const LAST_NAME_FIELD = {
  type: "text",
  label: "Last Name",
  name: "lastName",
};

const EMAIL_FIELD = {
  type: "email",
  label: "Work Email",
  name: "workEmail",
};

const COMPANY_FIELD = {
  type: "singleSelect",
  label: "Company",
  name: "companyId",
};

const ROLES_FIELD = {
  type: "multiSelect",
  label: "Roles",
  name: "roles",
  options: [
    { value: "onboarder", label: "Onboarder" },
    { value: "manager", label: "Manager" },
    { value: "people_team", label: "People Team" },
    { value: "basic", label: "Basic Employee" },
    { value: "company_admin", label: "Company Admin" },
    { value: "it_admin", label: "IT Admin" },
  ],
};

interface SimpleAdminEmployeeFormProps {
  onClose: () => void;
  updateCacheKey?: (cacheKey: string) => void;
  onSuccess?: () => void;
}

export default function SimpleAdminEmployeeForm({
  updateCacheKey = undefined,
  onClose,
  onSuccess = undefined,
}: SimpleAdminEmployeeFormProps) {
  const {
    control,
    setError,
    formState: { errors, isDirty },
    handleSubmit,
    reset,
    setFocus,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(simpleEmployeeForm),
  });
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // setTimeout necessary to defer setFocus until microtask queue is empty (event loop weirdness)
  useEffect(() => {
    setTimeout(() => setFocus("firstName"), 0);
  }, [setFocus]);

  /* HandleOnSubmit */
  const onSubmit = async (data: any) => {
    try {
      setIsLoading(true);
      const responseData = await apiClient.post<{ id: string }>(API_ROUTES.admin.users.base, { user: data });
      setIsLoading(false);

      onClose();
      if (updateCacheKey) {
        updateCacheKey(responseData.id);
      }
      if (onSuccess) {
        onSuccess();
      }
      toast({
        title: "New Employee Created.",
        status: "success",
      });
      reset({
        firstName: "",
        lastName: "",
        workEmail: "",
        companyId: "",
        roles: [],
      });
    } catch (error) {
      setIsLoading(false);
      // have to add a user-defined type guard in case its not a NetworkingError
      // https://www.typescriptlang.org/docs/handbook/2/narrowing.html#using-type-predicates
      if (isNetworkingError(error)) {
        if (error.status === 409) {
          setError("workEmail", {
            type: `${error.status}`,
            message: `A user with the email address ${data.workEmail} already exists.`,
          });
        } else {
          setError("workEmail", {
            type: `${error.status}`,
            message: `An error occurred. Please try again.`,
          });
        }
      }
    }
  };

  return (
    <Stack spacing="4">
      <GenericAsyncSelectField
        fetchPath={API_ROUTES.admin.companies.base}
        extractValue={(c: Company) => c.id}
        embedOption={(c) => ({
          value: c.id,
          label: `${c.name} (${c.domain})`,
          rawValue: c,
        })}
        control={control}
        {...COMPANY_FIELD}
        errors={errors}
      />
      <FloatingField name={FIRST_NAME_FIELD.name} errors={errors.firstName?.message}>
        <FloatingField.Label>{FIRST_NAME_FIELD.label}</FloatingField.Label>
        <FloatingField.Text control={control} name={FIRST_NAME_FIELD.name} />
      </FloatingField>
      <FloatingField name={LAST_NAME_FIELD.name} errors={errors.lastName?.message}>
        <FloatingField.Label>{LAST_NAME_FIELD.label}</FloatingField.Label>
        <FloatingField.Text control={control} name={LAST_NAME_FIELD.name} />
      </FloatingField>
      <FloatingField name={EMAIL_FIELD.name} errors={errors.workEmail?.message} required>
        <FloatingField.Label>{EMAIL_FIELD.label}</FloatingField.Label>
        <FloatingField.Text control={control} name={EMAIL_FIELD.name} type={EMAIL_FIELD.type} />
      </FloatingField>
      <FloatingField name={ROLES_FIELD.name} errors={(errors as any).roles?.message} required>
        <FloatingField.Label>{ROLES_FIELD.label}</FloatingField.Label>
        <FloatingField.MultiSelect
          control={control}
          name={ROLES_FIELD.name}
          options={ROLES_FIELD.options}
          menuPlacement="top"
        />
      </FloatingField>
      <ButtonGroup display="flex" justifyContent="flex-end">
        <ProceedCancelButtons
          handleSubmit={handleSubmit(onSubmit)}
          onClose={onClose}
          proceedText="Create Employee"
          proceedDisabled={!isDirty}
          isLoading={isLoading}
        />
      </ButtonGroup>
    </Stack>
  );
}
